import { groupBy, uniqBy } from 'es-toolkit';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import createPersistedState from 'use-persisted-state';

import { SongPreview } from '~/interfaces';
import useBaseUnitPx from '~/modules/hooks/use-base-unit-px';
import { serverRpc } from '~/modules/remote-mic/network/client';
import { useSubscription } from '~/modules/remote-mic/network/client/hooks/use-subscription';
import { transportErrorReason } from '~/modules/remote-mic/network/client/network-client';
import { RemoteQueueEntry } from '~/modules/remote-mic/network/client/subscriptions';
import { useLanguageList } from '~/modules/songs/hooks/use-language-list';
import useTaggedSongIndex from '~/modules/songs/hooks/use-tagged-song-index';
import SongDao from '~/modules/songs/songs-service';
import isE2E from '~/modules/utils/is-e2-e';
import { usePhoneToast } from '~/routes/remote-mic/components/phone-toast-context';
import usePermissions from '~/routes/remote-mic/hooks/use-permissions';
import useRemoteMicId from '~/routes/remote-mic/hooks/use-remote-mic-id';
import LanguageFilter from '~/routes/remote-mic/panels/remote-song-list/language-filter';
import { createQueueConfirmations } from '~/routes/remote-mic/panels/remote-song-list/queue-confirmations';
import RemoteFilterSheet from '~/routes/remote-mic/panels/remote-song-list/remote-filter-sheet';
import SongListHeader, { SongListTab } from '~/routes/remote-mic/panels/remote-song-list/song-list-header';
import { EmptyState, SkeletonRows } from '~/routes/remote-mic/panels/remote-song-list/song-list-states';
import { ArtistGroupRow, PhoneSong, QueueRow, SongRow } from '~/routes/remote-mic/panels/remote-song-list/song-rows';
import { useMySongList } from '~/routes/remote-mic/panels/remote-song-list/use-my-song-list';
import useRemoteFilters from '~/routes/remote-mic/panels/remote-song-list/use-remote-filters';
import { ConnectionStatuses } from '~/routes/remote-mic/remote-mic';
import { CustomVirtualization } from '~/routes/sing-a-song/song-selection/components/custom-virtualization';
import { searchList } from '~/routes/sing-a-song/song-selection/hooks/use-song-list-filter';

interface Props {
  roomId: string | null;
  connectionStatus: ConnectionStatuses;
  connectionError?: transportErrorReason;
  isKeepAwakeOn: boolean;
  setIsKeepAwakeOn: (keepAwake: boolean) => void;
  monitoringStarted: boolean;
  setMonitoringStarted: (micMonitoring: boolean) => void;
  /** Whether this panel is the tab on screen. All three stay mounted, so it has to be told. */
  isActive?: boolean;
}
const useExcludedLanguages = createPersistedState<string[]>('remote-mic-excluded-languages');
const useSelectedTab = createPersistedState<SongListTab>('remote-mic-song-list-tab');
const useSearchState = createPersistedState<string>('remote-mic-song-list-search');

// Today's artist grouping: at 500+ songs, an artist with three or more collapses into one row.
const GROUPING_THRESHOLD = isE2E() ? 5 : 500;
const GROUP_SIZE = isE2E() ? 2 : 3;

const NO_QUEUE: RemoteQueueEntry[] = [];

const getMainArtistName = (artist: string) => artist.split(' feat')[0];
const isArtistPresent = (a: string, b: string[]) => b.some((artist) => artist.toLowerCase() === a.toLowerCase());

function RemoteSongList({ connectionStatus, isActive = true }: Props) {
  const keyboard = useSubscription('keyboard-layout');
  const queue = useSubscription('song-queue') ?? NO_QUEUE;
  const permissions = usePermissions();
  const myMicId = useRemoteMicId();
  const flash = usePhoneToast();
  const originalSongList = useTaggedSongIndex();
  const [tab, setTab] = useSelectedTab('all');
  const [search, setSearch] = useSearchState('');
  const [excludedLanguages, setExcludedLanguages] = useExcludedLanguages([]);
  const { savedSongList, toggleSong, owner } = useMySongList();
  const { filters, setFilters, filterSongs, activeCount } = useRemoteFilters();
  const [sheet, setSheet] = useState<'filters' | 'languages' | null>(null);

  type SongListOverrides = Awaited<ReturnType<typeof serverRpc.songs.getSongList>>;
  const [overrides, setOverrides] = useState<SongListOverrides | undefined>();
  const [fetchError, setFetchError] = useState(false);
  // Reset the error flag when the connection is re-established so fetch is retried
  useEffect(() => {
    if (connectionStatus === 'connected') {
      setFetchError(false);
    }
  }, [connectionStatus]);
  useEffect(() => {
    if (connectionStatus === 'connected' && overrides === undefined && !fetchError) {
      serverRpc.songs
        .getSongList()
        .then(setOverrides)
        .catch((err) => {
          console.warn(err);
          setFetchError(true);
        });
    }
  }, [overrides, connectionStatus, fetchError]);

  // Asked again each time the tab comes back. Hiding a song or adding one happens on the TV and the
  // phone is never told, so the list would keep whatever it read when it first connected - this used
  // to be free, because leaving the tab unmounted the panel and coming back re-read it.
  const [wasActive, setWasActive] = useState(isActive);
  if (isActive !== wasActive) {
    setWasActive(isActive);
    if (isActive) {
      setOverrides(undefined);
      // A failure while the phone was disconnected must not make the list permanently stale.
      setFetchError(false);
    }
  }

  const songList = useMemo(
    () =>
      uniqBy(
        originalSongList.data
          .map((song) => ({ ...song, id: SongDao.generateSongFile(song) }))
          .filter((song) => !(overrides?.deleted ?? []).includes(song.id))
          .map(
            (song) =>
              ({
                id: song.id,
                artist: song.artist,
                title: song.title,
                video: song.video,
                language: song.language,
                search: song.search,
                /*
                 * Songs are narrowed to the shape the custom-song RPC uses so both kinds can sit in
                 * one list, and that narrowing was quietly dropping every field the search reads
                 * beyond artist and title. Typing a genre or a series worked on the TV and found
                 * nothing on the phone, which reads as the phone's search being broken.
                 */
                genre: song.genre,
                edition: song.edition,
                year: song.year,
                series: song.series,
                // Read by the Duets and Length filters. Dropping them here left both categories
                // unanswerable on the phone - every song looked like a solo song of unknown length.
                tracksCount: song.tracksCount,
                duration: song.duration,
              }) as SongListOverrides['custom'][number] &
                Pick<SongPreview, 'id'> &
                // Optional, because the custom songs concatenated on below carry none of them.
                Partial<Pick<SongPreview, 'genre' | 'edition' | 'year' | 'series' | 'tracksCount' | 'duration'>>,
          )
          .concat(...(overrides?.custom ?? []).map((song) => ({ ...song, id: SongDao.generateSongFile(song) }))),
        (song) => song.id,
      ),
    [originalSongList.data, overrides],
  );

  const changeTab = (next: SongListTab) => {
    setTab(next);
    setSearch('');
  };

  // Typing on the Queue tab is looking for something to queue, so it goes to the whole list.
  const changeSearch = (value: string) => {
    setSearch(value);
    if (value !== '' && tab === 'queue') setTab('all');
  };

  const savedSongs = useMemo(
    () => savedSongList.map((id) => songList.find((song) => song.id === id)).filter((song) => song !== undefined),
    [savedSongList, songList],
  );

  const filteredSongList = useMemo(() => {
    const list = tab === 'saved' ? savedSongs : songList;

    // A search reaches past the language filter, so a song you can name is findable with its language
    // off. `searchList` hands the list itself back only when there is nothing to search for.
    const searchedList = searchList(list, search);
    if (searchedList !== list) return filterSongs(searchedList);

    // Hidden only when EVERY language a song carries is excluded, so an English/Polish duet
    // survives an English-only filter.
    //
    // Asking the Language category for a language reaches past this setting, the same way it does on
    // the TV - picking Polish and being shown nothing because Polish is switched off reads as a bug,
    // and `useFilterCategories` sets `skipExcludedLanguages` for exactly this.
    const visible = filters.skipExcludedLanguages
      ? list
      : list.filter((song) => !song.language.every((songLang: string | null) => excludedLanguages.includes(songLang!)));

    return filterSongs(visible);
  }, [search, songList, savedSongs, tab, excludedLanguages, filterSongs, filters.skipExcludedLanguages]);

  const groupedSongList = useMemo(() => {
    if (filteredSongList.length < GROUPING_THRESHOLD) {
      return filteredSongList;
    }
    const groups = groupBy(filteredSongList, (song) => getMainArtistName(song.artist).toLowerCase());

    return Object.values(groups)
      .map((group) => (group.length >= GROUP_SIZE ? [group] : group))
      .flat();
  }, [filteredSongList]);

  const [expandedArtists, setExpandedArtists] = useState<string[]>([]);
  useLayoutEffect(() => {
    setExpandedArtists([]);
  }, [search, songList, tab, excludedLanguages, filters]);

  const itemsToRender = useMemo(() => {
    const itemsToRender: typeof groupedSongList = [];

    for (const song of groupedSongList) {
      itemsToRender.push(song);
      if (Array.isArray(song) && expandedArtists.includes(getMainArtistName(song[0].artist))) {
        itemsToRender.push(...song);
      }
    }
    return itemsToRender;
  }, [groupedSongList, expandedArtists]);

  const languages = useLanguageList(songList);

  const queuePositions = useMemo(() => new Map(queue.map((entry, index) => [entry.id, index + 1])), [queue]);

  const [confirmations] = useState(() => createQueueConfirmations(flash));
  useEffect(() => confirmations.queueChanged(queue), [confirmations, queue]);
  useEffect(() => () => confirmations.dispose(), [confirmations]);

  const queueSong = (song: PhoneSong) => {
    const position = queuePositions.get(song.id);
    if (position !== undefined) {
      flash(`"${song.title}" is already #${position} in the queue`);
      return;
    }
    if (confirmations.isPending(song.id)) return;

    confirmations.sent(song.id, song.title);
    serverRpc.songs.queue(song.id).catch(() => confirmations.failed(song.id, song.title));
  };

  const unit = useBaseUnitPx();
  const canSelect = keyboard?.remote?.includes('select-song') && permissions === 'write';
  const canQueue = permissions === 'write';
  const loading = originalSongList.isLoading && songList.length === 0;
  const listName = owner ? `${owner}'s list` : 'Your list';

  const tabs = [
    { tab: 'all' as const, label: 'All songs', count: songList.length, test: 'all-songs-button' },
    { tab: 'saved' as const, label: listName, count: savedSongs.length, test: 'your-list-button' },
    { tab: 'queue' as const, label: 'Queue', count: queue.length, test: 'queue-tab-button' },
  ];

  const clearSearchAndFilters = () => {
    setSearch('');
    setFilters({});
  };

  const queueMinutes = Math.round(queue.reduce((total, entry) => total + (entry.duration ?? 0), 0) / 60);

  const renderBody = () => {
    if (loading) return <SkeletonRows />;

    if (tab === 'queue') {
      if (queue.length === 0) {
        return (
          <EmptyState
            test="queue-empty"
            icon="ic:baseline-queue-music"
            title="The queue is empty"
            body="Tap the queue button on any song to line it up after the current one."
            cta="Browse all songs"
            onCta={() => changeTab('all')}
          />
        );
      }

      return (
        <div className="h-full overflow-y-auto overscroll-contain" data-test="song-queue">
          <div className="flex items-baseline justify-between px-5 pt-3.5 pb-2">
            <span className="text-[0.8125rem] font-bold tracking-[0.08em] text-white/70 uppercase">Up next</span>
            <span className="text-[0.8125rem] text-white/65">
              {queue.length} {queue.length === 1 ? 'song' : 'songs'}
              {queueMinutes > 0 && ` · ${queueMinutes} min`}
            </span>
          </div>
          {queue.map((entry, index) => (
            <QueueRow
              key={entry.id}
              song={songList.find((song) => song.id === entry.id) ?? entry}
              position={index + 1}
              addedBy={entry.addedBy}
              mine={!!myMicId && entry.addedById === myMicId}
              canQueue={canQueue}
              onMoveUp={() => serverRpc.queue.moveUp(entry.id).catch(() => flash('The TV did not move it up'))}
              onRemove={() =>
                serverRpc.queue.remove(entry.id).then(
                  () => flash(`Took "${entry.title}" out of the queue`),
                  () => flash(`The TV did not take "${entry.title}" out`),
                )
              }
            />
          ))}
        </div>
      );
    }

    if (tab === 'saved' && savedSongs.length === 0 && search === '') {
      return (
        <EmptyState
          test="saved-empty"
          icon="ic:baseline-star-outline"
          title="Nothing saved yet"
          body={`Tap the star on any song to keep it in ${owner ? `${owner}'s list` : 'your list'}.`}
          cta="Browse all songs"
          onCta={() => changeTab('all')}
        />
      );
    }

    if (itemsToRender.length === 0) {
      return (
        <EmptyState
          test="song-list-empty"
          icon="ic:baseline-search-off"
          title="Nothing matches"
          body={search ? `No song or artist matches "${search}".` : 'Try fewer filters.'}
          cta="Clear search and filters"
          onCta={clearSearchAndFilters}
        />
      );
    }

    return (
      <CustomVirtualization
        forceRenderItem={-1}
        Footer={<div style={{ height: unit * 3.75 }} className="landscap:block hidden" />}
        overScan={200}
        components={{}}
        context={{}}
        groupSizes={[itemsToRender.length]}
        groupHeaderHeight={1}
        groupContent={() => null}
        itemHeight={unit * 4.75}
        itemContent={(index, _groupIndex, itemProps) => {
          const song = itemsToRender[index];

          if (Array.isArray(song)) {
            const mainArtistName = getMainArtistName(song[0].artist);
            const isExpanded = isArtistPresent(mainArtistName, expandedArtists);

            return (
              <ArtistGroupRow
                artist={mainArtistName}
                count={song.length}
                expanded={isExpanded}
                onToggle={() =>
                  setExpandedArtists((artists) =>
                    isExpanded ? artists.filter((artist) => artist !== mainArtistName) : [...artists, mainArtistName],
                  )
                }
                rowProps={itemProps}
              />
            );
          }

          return (
            <SongRow
              song={song}
              saved={savedSongList.includes(song.id)}
              queuedAt={queuePositions.get(song.id) ?? 0}
              indented={isArtistPresent(getMainArtistName(song.artist), expandedArtists)}
              canQueue={canQueue}
              canSelect={!!canSelect}
              owner={owner}
              onStar={() => toggleSong(song.id)}
              onQueue={() => queueSong(song)}
              onSelect={() => void serverRpc.songs.select(song.id)}
              rowProps={itemProps}
            />
          );
        }}
      />
    );
  };

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
      <SongListHeader
        search={search}
        onSearchChange={changeSearch}
        tabs={tabs}
        tab={tab}
        onTabChange={changeTab}
        activeFilterCount={activeCount}
        onOpenFilters={() => setSheet('filters')}
        shownLanguages={
          excludedLanguages.length > 0 ? Math.max(0, languages.length - excludedLanguages.length) : undefined
        }
        onOpenLanguages={() => setSheet('languages')}
      />
      <RemoteFilterSheet
        open={sheet === 'filters'}
        onClose={() => setSheet(null)}
        catalogue={songList}
        languageSource={songList}
        resultCount={filteredSongList.length}
        filters={filters}
        setFilters={setFilters}
      />
      <LanguageFilter
        open={sheet === 'languages'}
        onClose={() => setSheet(null)}
        excludedLanguages={excludedLanguages}
        languageList={languages}
        onListChange={setExcludedLanguages}
      />
      <div className="min-h-0 flex-1">{renderBody()}</div>
    </div>
  );
}
export default RemoteSongList;
